import { Typography } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback } from 'react';
import { UseFormReturn, useFormContext } from 'react-hook-form';
import { KpiConfigPeriodToName } from '../../../data-models/company-financials.data-model';
import { IField } from '../../../data-models/field2.data-model';
import { MaggieFeatureFlags } from '../../../util/feature-flags';
import { IForm } from '../../../view-models/form.view-model';
import { ConfigFormFields } from '../../Config/Common/ConfigModal';
import { getKpiFormFields, hideKpiPeriodsField } from '../../Config/KPI/KPIFields';
import { StepperFormButtons } from '../../Finance2/Forms/StepperFormButtons';
import { kpiFieldFormStepState } from './KpiFieldFormStateJ';

interface KpiFieldFormStep1Props {
  form: IForm<IField<unknown>>;
  onCancel: () => void;
}

/** The standard schema-driven KPI form; Next advances only when validation passes. */
export function KpiFieldFormStep1({ form, onCancel }: KpiFieldFormStep1Props) {
  const methods = useFormContext<IField<unknown>>();
  const flags = useFlags<MaggieFeatureFlags>();
  const periods = methods.watch('periods') ?? [];

  const stepIsValid = useCallback(
    () =>
      new Promise<boolean>((resolve) => {
        void methods.handleSubmit(
          () => resolve(true),
          () => resolve(false)
        )();
      }),
    [methods]
  );

  // Display is configured in fieldDisplayConfig (step 2), so with the flag on the
  // Period picker disappears and periods remain purely a computation setting.
  const dynamicFormTransformer = useCallback(
    (
      staticForm: IForm<IField<unknown>>,
      formMethods: UseFormReturn<IField<unknown>>,
      featureFlags: MaggieFeatureFlags
    ) => {
      const dynamicForm = getKpiFormFields(staticForm, formMethods, featureFlags);
      if (featureFlags.showFieldsManager) {
        hideKpiPeriodsField(dynamicForm, formMethods);
      }
      return dynamicForm;
    },
    []
  );

  return (
    <>
      <ConfigFormFields form={form} dynamicFormTransformer={dynamicFormTransformer} />
      {flags.showFieldsManager && periods.length > 0 && (
        <Typography variant='caption' color='text.secondary'>
          This KPI will be computed for {periods.map((p) => KpiConfigPeriodToName[p]).join(', ')}. Choose
          which columns appear in Company Views on the next step.
        </Typography>
      )}
      <StepperFormButtons
        stepState={kpiFieldFormStepState}
        stepIsValid={stepIsValid}
        handleGoBack={onCancel}
        backButtonLabel='Cancel'
      />
    </>
  );
}
